/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
/* eslint-disable react/jsx-props-no-spreading */

import React, { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Ace } from "ace-builds";
import { Button, Modal } from "react-bootstrap";
import AceEditor from "react-ace";
import {
  SCRIPT,
  SupportDocsLink,
  TransformData,
  TransformFieldProps,
} from "@advapacs/common";

import ace from "ace-builds/src-noconflict/ace";
import "ace-builds/src-noconflict/mode-javascript"; // Load the language definition file
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/theme-github_dark"; // Load the theme definition file
import { getSubDomain, jsStoreReplaceFunction } from "../../../helpers/index";
import ExternalError from "./ExternalError";

const urlDecode = new URL(
  "ace-builds/worker-javascript.js",
  window.location.origin
).toString();
const path = jsStoreReplaceFunction(urlDecode, getSubDomain());
ace.config.setModuleUrl("ace/mode/javascript_worker", path);

const Script: FunctionComponent<TransformFieldProps> = ({
  disabled,
  id,
  errors,
  transform,
  onValueChangeHandler,
  onHandleScriptModal,
  isDark = false,
}: TransformFieldProps) => {
  const { t } = useTranslation("form");
  const [editing, setEditing] = useState(false);
  const [editorValue, setEditorValue] = useState<string | undefined>(
    transform?.script
  );
  const [annotations, setAnnotations] = useState<Ace.Annotation[]>([]);
  const dispatchNewValue = (trans: TransformData) => {
    onValueChangeHandler({
      type: SCRIPT,
      id,
      transform: trans,
    });
  };
  const updateValue = () => {
    dispatchNewValue({ ...transform, script: editorValue, annotations });
    setEditing(false);
    if (onHandleScriptModal) onHandleScriptModal(false);
  };

  const updateEditorValue = (value: string) => {
    setEditorValue(value);
  };

  const validateScript = (annos: Ace.Annotation[]) => {
    setAnnotations(annos);
  };
  const hasError = () => {
    return (
      errors.find((item) => item.id === id && item.type === SCRIPT) !==
      undefined
    );
  };

  useEffect(() => {
    ace.config.set("basePath", "/node_modules/ace-builds/src-noconflict/");
    ace.config.set("loadWorkerFromBlob", false); // Disable loading workers from blobs
    // Set the worker path for ace-builds
    // ace.config.setModuleUrl(
    //   "ace/mode/javascript_worker",
    //   new URL("ace-builds/src-noconflict/worker-javascript.js", import.meta.url)
    // );
  }, []);

  return (
    <>
      <div className="row">
        <div className="col">
          <Button
            variant="secondary"
            className="add-condition"
            onClick={() => {
              setEditing(true);
              if (onHandleScriptModal) onHandleScriptModal(true);
            }}
          >
            {t("view_script")}
          </Button>
          <Modal show={editing} centered dialogClassName="full-modal-dialog">
            <Modal.Header className="justify-content-start">
              <Modal.Title>{t("script_editor")}</Modal.Title>
              <SupportDocsLink path="/configuration/transform-rules" />
            </Modal.Header>
            <Modal.Body>
              <AceEditor
                className={`ace-editor-custom ${isDark ? "dark" : "light"}`}
                defaultValue={transform?.script}
                mode="javascript"
                theme={isDark ? "github_dark" : "github"}
                fontSize={14}
                onChange={updateEditorValue}
                name={`script_${id}`}
                onValidate={validateScript}
                editorProps={{
                  $blockScrolling: true,
                }}
                setOptions={{
                  readOnly: disabled,
                }}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="light"
                onClick={() => {
                  setEditing(false);
                  if (onHandleScriptModal) onHandleScriptModal(false);
                }}
              >
                {t("close")}
              </Button>
              {!disabled && (
                <Button variant="primary" onClick={updateValue}>
                  {t("save")}
                </Button>
              )}
            </Modal.Footer>
          </Modal>
          {hasError() && !transform?.script && (
            <label
              className="validation-invalid-label"
              htmlFor={`${id}_script`}
            >
              {t("rule:script_required")}
            </label>
          )}
          {hasError() &&
            transform?.annotations &&
            transform.annotations.length > 0 &&
            transform.annotations.filter((ann) => ann.type === "error").length >
              0 && (
              <label
                className="validation-invalid-label"
                htmlFor={`${id}_script`}
              >
                {t("rule:script_invalid")}
              </label>
            )}
          <ExternalError
            errors={errors}
            id={id}
            field="script"
            htmlFor={`${id}_script`}
          />
        </div>
      </div>
    </>
  );
};

export default Script;
